'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { getBookingUrl } from '@/lib/booking';
import { useLocale, useLocalizedPath, useSwitchLocalePath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const switchLocalePath = useSwitchLocalePath();
  const text = getPublicStrings(locale).header;
  const bookingHref = getBookingUrl('nav');
  const bookingExternal = bookingHref.startsWith('http');
  const isNl = locale === 'nl';
  const otherLocalePath = switchLocalePath(isNl ? 'en' : 'nl');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const primaryDesktopLinks = text.primaryLinks;
  const secondaryDesktopLinks = text.secondaryLinks;

  const mobileNavLinks = [
    { id: 'nav_home', label: text.homeLabel, href: '/#hero' },
    ...primaryDesktopLinks,
    ...secondaryDesktopLinks,
  ];

  return (
    <nav
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'border-b border-border bg-white/80 shadow-sm backdrop-blur-xl'
          : 'bg-transparent'
      }`}
      role="navigation"
      aria-label={text.ariaMainNavigation}
    >
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6 md:h-24 md:px-12">
        <Link href={localizePath('/#hero')} className="group flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-gold md:h-12 md:w-12">
            <Icon name="TrophyIcon" variant="solid" size={24} className="text-primary" />
          </div>
          <span className="font-heading text-lg font-bold uppercase tracking-tight text-primary md:text-xl">
            {text.academyName}
          </span>
        </Link>

        <div className="hidden items-center gap-4 lg:flex">
          <div className="flex items-center gap-5 text-[10px] font-bold uppercase tracking-widest">
            {primaryDesktopLinks?.map((link) => (
              <Link
                key={link?.id}
                href={localizePath(link?.href)}
                className="text-foreground/80 transition-colors hover:text-primary"
              >
                {link?.label}
              </Link>
            ))}
          </div>

          <details className="group relative">
            <summary className="flex cursor-pointer list-none items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-foreground/80 transition-colors hover:text-primary">
              {text.moreLabel}
              <Icon
                name="ChevronDownIcon"
                size={14}
                className="transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="absolute right-0 top-7 z-50 min-w-[180px] rounded-md border border-border bg-white p-2 shadow-xl">
              {secondaryDesktopLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={localizePath(link?.href)}
                  className="block rounded px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-foreground/80 transition-colors hover:bg-background hover:text-primary"
                >
                  {link?.label}
                </Link>
              ))}
            </div>
          </details>

          <Link
            href={otherLocalePath}
            className="text-[10px] font-black uppercase tracking-widest text-foreground/70 hover:text-primary"
            hrefLang={isNl ? 'en' : 'nl'}
          >
            {text.languageSwitchLabel}
          </Link>

          <a
            href={bookingHref}
            {...(bookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="inline-flex min-w-[168px] items-center justify-center bg-gold px-6 py-3.5 text-[10px] font-black uppercase leading-none tracking-[0.16em] text-gold-foreground whitespace-nowrap transition-all hover:bg-gold/90 hover:shadow-lg hover:shadow-gold/25"
          >
            {text.bookNow}
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 lg:hidden"
          aria-label={isMobileMenuOpen ? text.closeMenu : text.openMenu}
          aria-expanded={isMobileMenuOpen}
        >
          <Icon
            name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
            size={28}
            className="text-primary"
          />
        </button>
      </div>
      {isMobileMenuOpen && (
        <div className="border-t border-border bg-white lg:hidden">
          <div className="max-h-[calc(100vh-5rem)] space-y-4 overflow-y-auto px-6 py-8">
            {mobileNavLinks?.map((link) => (
              <Link
                key={link?.id}
                href={localizePath(link?.href)}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-sm font-bold uppercase tracking-widest text-foreground transition-colors hover:text-primary"
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href={otherLocalePath}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-sm font-bold uppercase tracking-widest text-foreground/70"
            >
              {text.languageSwitchLabel}
            </Link>
            <a
              href={bookingHref}
              {...(bookingExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full bg-gold py-3 text-center text-[10px] font-black uppercase tracking-widest text-gold-foreground"
            >
              {text.bookNow}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
